import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { Spinner } from "@/components/ui/spinner";
import TaskStepsProgress from "@/components/ui/task-steps-progress/TaskStepsProgress";
import RefreshPreviewDialog from "@/components/ui/refresh-preview-dialog/RefreshPreviewDialog";
import { useTaskCancelResume } from "@/hooks/useTaskCancelResume";
import { useTaskStream } from "@/hooks/useTaskStream";
import { previewRefreshPlan, executeRefreshPlan } from "@/services/api/data-hub/refreshPlan";
import type { RefreshPlanPreview, RefreshPlanPreviewRequest } from "@/services/api/data-hub/types";

// --- Preview request params (passed from callers) ---

export interface RefreshPreviewParams {
  resource_ids: string[];
  recalculate_portfolio_ids?: string[];
  title?: string;
  include_month_end?: boolean;
  propagate_to_holdings?: boolean;
}

type DialogMode = "idle" | "loading_preview" | "preview" | "executing" | "tracking";

// --- Context interface ---

interface TaskProgressDialogContextType {
  openTaskProgress: (taskIds: string[], title?: string) => void;
  openRefreshPreview: (params: RefreshPreviewParams) => void;
}

const TaskProgressDialogContext = createContext<TaskProgressDialogContextType | undefined>(undefined);

export const useTaskProgressDialog = (): TaskProgressDialogContextType => {
  const ctx = useContext(TaskProgressDialogContext);
  if (!ctx) throw new Error("useTaskProgressDialog must be used within TaskProgressDialogProvider");
  return ctx;
};

/** Same as useTaskProgressDialog but returns null when the provider is not mounted (e.g. OS window edge cases). */
export const useTaskProgressDialogOptional = (): TaskProgressDialogContextType | null => {
  return useContext(TaskProgressDialogContext) ?? null;
};

export const TaskProgressDialogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // --- Shared state ---
  const [dialogTitle, setDialogTitle] = useState("Details");
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>("idle");

  // --- Task tracking state ---
  // rootTaskIds: the initially-provided tasks (steps come from these only).
  // dialogTaskIds: root + chained children (tracked for completion/cancellation).
  const [rootTaskIds, setRootTaskIds] = useState<string[]>([]);
  const [dialogTaskIds, setDialogTaskIds] = useState<string[]>([]);
  const { isPolling, overallProgress, statusMessage, isComplete, hasFailed, isCancelled, steps, childTaskIds, isLoadingInitial } = useTaskStream(dialogTaskIds, { stepTaskIds: rootTaskIds });
  const { onStop, onResume, cancelRequested, resumeRequested } = useTaskCancelResume(dialogTaskIds, rootTaskIds);

  // --- Preview state ---
  const [preview, setPreview] = useState<RefreshPlanPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Chain child tasks (e.g. snapshot refresh after portfolio recalculation)
  const chainedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (childTaskIds.length === 0) return;

    const newIds = childTaskIds.filter((id) => !chainedRef.current.has(id));
    if (newIds.length === 0) return;

    for (const id of newIds) chainedRef.current.add(id);
    console.info("[TaskProgress]", `Chaining ${newIds.length} child task(s): ${newIds.map((id) => id.slice(0, 8)).join(", ")} (${chainedRef.current.size} total)`);
    setDialogTaskIds((prev) => [...prev, ...newIds]);
  }, [childTaskIds]);

  // --- Reset helpers ---

  const _resetAll = useCallback(() => {
    setMode("idle");
    setRootTaskIds([]);
    setDialogTaskIds([]);
    setPreview(null);
    setPreviewError(null);
    chainedRef.current = new Set();
  }, []);

  // --- openTaskProgress (existing behaviour) ---

  const openTaskProgress = useCallback((taskIds: string[], title?: string) => {
    _resetAll();
    setRootTaskIds(taskIds);
    setDialogTaskIds(taskIds);
    if (title) setDialogTitle(title);
    setMode("tracking");
    setIsOpen(true);
  }, [_resetAll]);

  const executePlan = useCallback(async (planPreview: RefreshPlanPreview) => {
    setMode("executing");
    try {
      const response = await executeRefreshPlan(planPreview.plan);

      chainedRef.current = new Set();
      setRootTaskIds(response.task_ids);
      setDialogTaskIds(response.task_ids);
      setMode("tracking");
    } catch (err) {
      console.error("[TaskProgress] Execute failed:", err);
      setPreviewError(err instanceof Error ? err.message : "Failed to execute refresh plan");
      setMode("loading_preview");
    }
  }, []);

  // --- openRefreshPreview (new) ---

  const openRefreshPreview = useCallback(async (params: RefreshPreviewParams) => {
    _resetAll();
    setDialogTitle(params.title ?? "Refresh plan");
    setMode("loading_preview");
    setIsOpen(true);

    try {
      const request: RefreshPlanPreviewRequest = {
        resource_ids: params.resource_ids,
        recalculate_portfolio_ids: params.recalculate_portfolio_ids,
        include_month_end: params.include_month_end,
        propagate_to_holdings: params.propagate_to_holdings,
      };

      const result = await previewRefreshPlan(request);
      setPreview(result);
      setMode("preview");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load refresh plan";
      console.error("[TaskProgress] Preview failed:", err);
      setPreviewError(message);
    }
  }, [_resetAll]);

  // --- Preview confirm handler ---

  const handlePreviewConfirm = useCallback(async () => {
    if (!preview) return;
    await executePlan(preview);
  }, [executePlan, preview]);

  // --- Dialog close / cancel ---

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (!isPolling) _resetAll();
  }, [isPolling, _resetAll]);

  const handlePreviewCancel = useCallback(() => {
    setIsOpen(false);
    _resetAll();
  }, [_resetAll]);

  // --- Context value ---

  const value = useMemo(
    () => ({ openTaskProgress, openRefreshPreview }),
    [openTaskProgress, openRefreshPreview],
  );

  // --- Render ---

  const isTrackingMode = mode === "tracking";
  const isPreviewMode = mode === "preview";
  const isLoadingMode = mode === "loading_preview" || mode === "executing";

  return (
    <TaskProgressDialogContext.Provider value={value}>
      {children}

      {/* --- Preview dialog --- */}
      {isPreviewMode && preview && (
        <RefreshPreviewDialog
          open={isOpen}
          title={dialogTitle}
          steps={preview.steps}
          totalResources={preview.total_resources}
          onConfirm={handlePreviewConfirm}
          onCancel={handlePreviewCancel}
        />
      )}

      {/* --- Loading / executing spinner --- */}
      {isLoadingMode && (
        <Dialog open={isOpen} onClose={handlePreviewCancel} size="3xl">
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogBody>
            {previewError ? (
              <p className="text-sm text-red-600 dark:text-red-400">{previewError}</p>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" label={mode === "executing" ? "Starting..." : "Loading plan..."} />
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <Button color="slate" onClick={handlePreviewCancel}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* --- Task tracking dialog (existing) --- */}
      {isTrackingMode && (
        <Dialog open={isOpen} onClose={handleClose} size="3xl">
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogBody>
            <TaskStepsProgress
              title={dialogTitle}
              steps={steps}
              overallProgress={overallProgress}
              isComplete={isComplete}
              hasFailed={hasFailed}
              isCancelled={isCancelled}
              statusMessage={statusMessage}
              isLoadingInitial={isLoadingInitial}
              showStop={isPolling && !isCancelled}
              showResume={isCancelled}
              onStop={onStop}
              onResume={onResume}
              stopPending={cancelRequested}
              resumePending={resumeRequested}
            />
          </DialogBody>
          <DialogActions>
            {(isPolling || isCancelled) && (
              <p className="mr-auto text-xs text-slate-400 dark:text-slate-500">
                {isCancelled
                  ? "Use ▶ on the progress bar to resume from the last checkpoint."
                  : "You can close this dialog \u2014 the process will run in the background."}
              </p>
            )}
            <Button color="indigo" onClick={handleClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </TaskProgressDialogContext.Provider>
  );
};
